import React, { useEffect } from "react";
import { func, object, shape, string } from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { useToasts } from "react-toast-notifications";
import { useDispatch } from "react-redux";

import BaseForm from "components/forms/Base";
import FormControl from "components/forms/shared/Control";
import FormGroup from "components/forms/shared/FormGroup";
import FormPrimaryButton from "components/buttons/forms/FormPrimary";
import FormSubmitContainer from "components/forms/shared/SubmitContainer";
import { updateProject } from "features/projects/thunks";
import { Project } from "types";

function ProjectSingleFieldForm({
  project,
  closeModal,
  label,
  fieldName,
  controllerProps,
  ...props
}) {
  /** Flexible form to update a single project field. */

  const {
    handleSubmit,
    control,
    errors,
    reset,
    formState: { isSubmitting },
  } = useForm(
    {
      defaultValues: project,
    },
    [project]
  );
  const dispatch = useDispatch();
  const { addToast } = useToasts();

  useEffect(() => {
    reset(project);
  }, [reset, project]);

  const onSubmit = async (data) => {
    const action = await dispatch(
      updateProject({ projectId: project.id, payload: data })
    );

    if (action.type === "UPDATE_PROJECT/fulfilled") {
      addToast("Project updated successfully", { appearance: "success" });
      closeModal();
    } else if (action.type === "UPDATE_PROJECT/rejected") {
      addToast("Error updating project", { appearance: "error" });
    }

    return action;
  };

  return (
    <BaseForm onSubmit={handleSubmit(onSubmit)} {...props}>
      <FormGroup errors={errors[fieldName]}>
        <Controller
          as={FormControl}
          control={control}
          name={fieldName}
          isInvalid={errors[fieldName] !== undefined}
          defaultValue=""
          {...controllerProps}
        />
      </FormGroup>
      <FormSubmitContainer>
        <FormPrimaryButton isLoading={isSubmitting}>Save</FormPrimaryButton>
      </FormSubmitContainer>
    </BaseForm>
  );
}

ProjectSingleFieldForm.propTypes = {
  /** The project for which we're editing a field */
  project: shape(Project).isRequired,

  /** The function to close the parent modal. */
  closeModal: func.isRequired,

  /** The field name that will be updated. */
  fieldName: string.isRequired,

  /** Extra props to pass to the controller. */
  controllerProps: object,

  /** The label to show with the form field. */
  label: string,
};

ProjectSingleFieldForm.defaultProps = {
  controllerProps: {},
  label: null,
};

export default ProjectSingleFieldForm;
